import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import { useAppOrgContext } from "../../context/AppOrgContext";
import OrgCreateAndAssignUsers from "./OrgCreateAndAssignUsers";
import Input from "../../components/FormComponents/Input";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import ButtonActionIcon from "../../components/BuilderComponents/ButtonActionIcon";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import BuilderTabs from "../../Layouts/BuilderTabs";
import AssignUserToApplication from "../Application/AssignUserToApplication";
import useQuery from "../../hooks/useQuery";
import { getOrganisationsApi } from "../../services/apis/organisationApis";
import UserActivityLogs from "./UserActivityLogs";
import UserAssignmentLogs from "./UserAssignmentLogs";
import AppAssignmentAudit from "./AppAssignmentAudit";

const UserManagement = () => {
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [appSearch, setAppSearch] = useState("");
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const { orgId } = useParams();
  const params = useParams();

  const { allApplications } = useAppOrgContext();

  // Fetch organizations
  const { data: organizationsData } = useQuery("organisations", ({ signal }) => getOrganisationsApi({ signal }));

  const organizations = organizationsData?.data || [];

  const handleApplicationSearch = (e) => {
    const value = e.target.value;
    setAppSearch(value);

    if (!value.trim()) {
      setFilteredApplications(getApplicationsForSelectedOrg());
      return;
    }

    const appsToFilter = getApplicationsForSelectedOrg();
    const filtered = appsToFilter.filter((app) => app.name.toLowerCase().includes(value.toLowerCase()));

    setFilteredApplications(filtered);
  };

  const getApplicationsForSelectedOrg = () => {
    if (!selectedOrganization) {
      return allApplications || [];
    }

    const selectedOrg = organizations.find((org) => org.id === selectedOrganization.id);
    return selectedOrg?.apps || [];
  };

  const handleOrganizationChange = (org) => {
    setSelectedOrganization(org);
    setAppSearch(""); // Clear search when organization changes
  };

  useEffect(() => {
    const applicationsToShow = getApplicationsForSelectedOrg();
    setFilteredApplications(applicationsToShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization, allApplications, organizations]);

  // Get the selected organization ID for passing to AssignUserToApplication
  const selectedOrgId = selectedOrganization?.id || orgId;

  const tabData = [
    {
      label: "User Management",
      content: (
        <div className="d_flex gap_4 h_100 p_3">
          <div className="h_100" style={{ width: "60%" }}>
            <OrgCreateAndAssignUsers />
          </div>
          <div className="h_100 builder_form" style={{ width: "40%", padding: "0 32px" }}>
            <h2 className="mt_2">Assign Users to Application</h2>

            <div className="d_flex justify_between mr_0 mx_0 gap_3 w_100">
              <div className="d_flex gap_3">
                {/* Organization Select Box */}
                <div style={{ minWidth: "250px" }}>
                  <SingleSelectBox
                    options={organizations}
                    valueKey="id"
                    labelKey="name"
                    selectedData={selectedOrganization}
                    onChange={handleOrganizationChange}
                    placeholder="Select Organization"
                    label="Organization"
                    isSearch={true}
                  />
                </div>
              </div>
              <div className="position_relative mb_3 mt_3">
                <Input
                  placeholder="Search Application"
                  value={appSearch}
                  onChange={handleApplicationSearch}
                  containerStyle={{ position: "relative", width: "100%" }}
                  inputBoxStyle={{ paddingLeft: "28px", height: "36px" }}
                />
                <MaterialIcon
                  name="search"
                  className="position_absolute font_size_large"
                  style={{ top: "8px", left: "8px" }}
                />
              </div>
            </div>

            <div className="user_table">
              <table>
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredApplications?.length > 0 ? (
                    filteredApplications.map((app) => (
                      <tr key={app.id}>
                        <td>{app.name}</td>
                        <td>
                          <ButtonActionIcon
                            type="group_add"
                            onClick={() => {
                              setSelectedApplication(app.id);
                              setAssignModalOpen(true);
                            }}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2}>
                        {selectedOrganization
                          ? "No Applications Found in Selected Organization"
                          : "Please Select an Organization"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
    },
    {
      label: "User Management Logs",
      content: (
        <div className="h_100 w_100 overflow_hidden p_3">
          <BuilderTabs
            tabs={[
              {
                label: "User Activity Logs",
                content: <UserActivityLogs />
              },
              {
                label: "User Assignment Logs",
                content: <UserAssignmentLogs />
              },
              {
                label: "System Assignment Logs",
                content: <AppAssignmentAudit />
              }
            ]}
            containerClass="themes_tabs"
            contentClass="h_100 w_100 overflow_auto"
          />
        </div>
      )
    }
  ];

  return (
    <div className="application_container">
      <AppLeftSideBar type={params.orgId ? "app" : "org"} />
      <div className="page_section overflow_hidden">
        <BuilderTabs
          tabs={tabData}
          tabsClass="h_100 w_fit"
          contentStyle={{ height: "calc(100% - 28px)", overflow: "auto" }}
        />
        <BuilderModal isOpen={assignModalOpen} onClose={() => setAssignModalOpen(false)} modalName="Assign Users">
          <AssignUserToApplication appId={selectedApplication} orgId={selectedOrgId} />
        </BuilderModal>
      </div>
    </div>
  );
};

export default UserManagement;
